import { useQuery } from '@tanstack/react-query';
import { erpApi, erpPendingChangeCountKey } from '../api/erp.api.js';
import { correctionsApi, correctionsQueryKey } from '../api/attendance.api.js';
import {
  PENDING_LEAVE_REQUESTS_QUERY_KEY,
  leaveRequestsApi,
} from '../api/leave.api.js';
import {
  LIVE_HEADCOUNT_QUERY_KEY,
  liveSiteBoardApi,
} from '../api/liveSiteBoard.api.js';
import { letterQueueQueryKey, lettersApi } from '../api/letters.api.js';
import { sumOnSiteNow } from '../routes/operations/operationsModel.js';

/*
 * THE NUMBERS THE RAIL SAYS OUT LOUD.
 *
 * Every hook here returns `number | null`, and `null` means WE DO NOT KNOW —
 * first load, a failed request, or a role/feature that never asks. It never
 * means zero (rule 3.3). The rail is the one place a reader glances rather
 * than reads, so a confident `0` over a failed feed is worse there than
 * anywhere else in the app: it says "nothing to do" about a queue nobody has
 * counted. The renderers turn `null` into an absence or an em-dash; none of
 * them turn it into a digit.
 *
 * NONE OF THESE IS A NEW ENDPOINT. Each one registers under the query key its
 * own screen already uses, so:
 *
 *   - the badge counts the rows that queue SHOWS, rather than a second
 *     server-side count with its own idea of a manager's scope (rule 3.6);
 *   - on the screen itself the rail is a second observer of one cache entry —
 *     no second request, and no way for the two to state different numbers
 *     (rule 3.2);
 *   - approving something on the page moves the rail with it, because the
 *     invalidation the page already runs is the invalidation the badge reads.
 *
 * What that costs is stated honestly: away from those screens the rail is the
 * only holder of the entry, so opening the Admin panel issues the requests. The
 * two queue lists are small (a queue that is not small is a queue nobody is
 * working) and are given a `staleTime` instead of a polling timer, so walking
 * around the console re-uses the cache rather than re-fetching per navigation.
 * The live headcount is the one that polls, because a live figure that does not
 * move is not one — see `useOnSiteNowCount`.
 */

/** How long a queue count may be re-used across navigations before it re-fetches. */
const QUEUE_STALE_MS = 60_000;

/**
 * Correction requests waiting on a decision, in the viewer's scope.
 *
 * Counts `status === 'PENDING'` exactly as `CorrectionsPage` computes its own
 * "open" list from the same payload — the server's `pending` filter should
 * already return only those, and the badge agreeing with the page matters more
 * than trusting that.
 */
export function usePendingCorrectionCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: correctionsQueryKey('pending'),
    queryFn: () => correctionsApi.list('pending'),
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || !data) return null;
  return data.filter((c) => c.status === 'PENDING').length;
}

/** Leave requests waiting on a decision — the rows /leave/manage lists. */
export function usePendingLeaveCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: PENDING_LEAVE_REQUESTS_QUERY_KEY,
    queryFn: () => leaveRequestsApi.list({ filter: 'all', status: 'PENDING' }),
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || !data) return null;
  return data.length;
}

/**
 * Letter requests waiting on a decision — the rows the Letters screen's
 * approvals tab lists, through that tab's own query key, so the two cannot
 * state different numbers.
 *
 * Gated by the caller on the `letters` feature and on whether the reader may
 * decide anything: on a tenant with the module off, or for a role the server
 * would refuse, this must issue no request rather than count a 403 as zero.
 */
export function usePendingLetterCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: letterQueueQueryKey('PENDING'),
    queryFn: () => lettersApi.list({ status: 'PENDING' }),
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || !data) return null;
  /* `total`, NOT the rows. The query already narrows to PENDING server-side, so
     re-filtering the page did nothing except cap the badge at the endpoint's
     200: a queue of 340 read "200" on the rail, which is the one number whose
     whole job is saying how much work is waiting. */
  return data.total;
}

/**
 * How many ERP changes are waiting on a decision.
 *
 * Its own endpoint rather than `listChanges(…).length`, and the difference is
 * not cosmetic: the queue is CAPPED at 200 rows, so counting the page would
 * report "200" over a first import that staged the whole workforce — which is
 * the one number whose entire job is saying how much work is waiting. The
 * letters badge shipped exactly that bug and this is the shape it was fixed to.
 *
 * `null` on a failed fetch, so the rail draws NO badge rather than a confident
 * zero. "Nothing is waiting" and "we could not ask" are different sentences.
 */
export function usePendingErpChangeCount(options: { enabled?: boolean } = {}): number | null {
  const { data, isError } = useQuery({
    queryKey: erpPendingChangeCountKey,
    queryFn: () => erpApi.pendingChangeCount(),
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || data === undefined) return null;
  return data;
}

/**
 * How many people are checked in at a worksite right now — the rail's live
 * figure, and the same number as the Operations page's "On site now" tile
 * because it is the same feed run through the same `sumOnSiteNow`.
 *
 * THIS ONE POLLS, at the 30s cadence the Operations page already polls it at,
 * and only while the tab is in front. A count beside a pulsing dot is a claim
 * that it is current; a cached figure from twenty minutes ago under that dot
 * would be the dishonest half of the two. On /operations the page owns the
 * timer and this observer is free; elsewhere it is the rail's own request, and
 * that is the one recurring cost this work adds.
 *
 * Gated by the caller on the `workers` pillar: this feed is per-WORKSITE, so an
 * office-only tenant has no such number and gets no dot at all rather than a
 * zero.
 */
export function useOnSiteNowCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: LIVE_HEADCOUNT_QUERY_KEY,
    queryFn: liveSiteBoardApi.liveHeadcount,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    enabled: options.enabled ?? true,
  });
  if (isError) return null;
  return sumOnSiteNow(data);
}
